import React, { FC } from "react";
import { Typography, Hidden, Divider } from "@mui/material";
import ItemizedCosts from "../ItemizedCosts/ItemizedCosts";
import sortUnits from "../../../util/sortUnits";
import getUnitLetter from "../../../util/letters";
import "../../CartCheck/CartCheck.css";
import { QuoteObj } from "../../../GlobalTypes";
import { styled } from "@mui/material/styles";
import ImageLayout from "./ImageLayout";
import Details from "./Details";
import Footer from "./Footer";
import dealerRoles from "../../../util/dealerAccounts";

const StyledWrapDiv = styled("div")({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  flexBasis: "65%",
  border: "2px solid grey",
  maxHeight: "60vh",
  marginRight: 20,
  backgroundColor: "white",
});

export const StyledColDiv = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  paddingLeft: 5,
});

const StyledColDiv2 = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
});

export const StyledRowDiv = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const StyledColDivEnd = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  flexDirection: "column",
  position: "relative",
});

const StyledColHidden = styled("div")({
  display: "flex",
  justifyContent: "space-around",
  position: "absolute",
  bottom: 0,
  width: "100%",
  backgroundColor: "white",
  zIndex: 2,
  borderTop: "1px solid lightgrey",
});
const StyledHeadDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  borderBottom: "1px solid #e0e0e0",
  [theme.breakpoints.down("lg")]: {
    display: "none",
    borderBottom: "none",
  },
}));

const StyledCartLayoutDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  paddingBottom: 10,
  paddingLeft: 20,
  paddingRight: 50,
  paddingTop: 10,
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    paddingBottom: 30,
  },
}));

const StyledOrderUnits = styled("div")({
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  paddingBottom: 15,
});

const StyledSummaryDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
  },
}));

const StyledDivqp = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  flexBasis: "30%",
  [theme.breakpoints.down("lg")]: {
    paddingTop: 40,
  },
}));

interface DoorListProps {
  signInRole: string;
  otsm: boolean;
  quote: QuoteObj;
}

const DoorList: FC<DoorListProps> = ({ signInRole, otsm, quote }) => {
  const orderedUnits = sortUnits(quote.units || []);

  const hasDealerAccess = dealerRoles.includes(signInRole);

  return (
    <StyledWrapDiv
      style={{
        height: !hasDealerAccess && signInRole !== "Admin" ? "60vh" : "",
      }}
    >
      <StyledHeadDiv>
        <Typography
          style={{
            flexBasis: !otsm || !hasDealerAccess ? "15%" : "21%",
          }}
          variant="body2"
        >
          Quantity
        </Typography>
        {(!otsm || !hasDealerAccess) && (
          <Typography style={{ flexBasis: "15%" }} variant="body2">
            Price
          </Typography>
        )}
      </StyledHeadDiv>
      <StyledOrderUnits className="scroll">
        {orderedUnits?.map((door, index) => (
          <div key={door._id}>
            <StyledCartLayoutDiv>
              <StyledSummaryDiv>
                <Hidden xlDown>
                  <Typography style={{ paddingRight: 10 }} variant="body2">
                    {getUnitLetter(index)}
                  </Typography>
                </Hidden>

                <Hidden mdDown>
                  <ImageLayout isSmallScreen={false} door={door} />
                </Hidden>
                <Hidden smUp>
                  <ImageLayout isSmallScreen={true} door={door} />
                </Hidden>

                <Details door={door} />
              </StyledSummaryDiv>
              <StyledDivqp>
                <Typography variant="body2">
                  <Hidden lgUp>
                    <Typography
                      style={{ display: "inline", fontWeight: "bold" }}
                      variant="body2"
                    >
                      QTY:{" "}
                    </Typography>
                  </Hidden>
                  {door.quantity || "1"}
                </Typography>
                {(!otsm || !hasDealerAccess) && (
                  <>
                    <StyledColDiv2>
                      <StyledColDivEnd>
                        <Typography variant="body2">
                          <Hidden lgUp>
                            <Typography
                              style={{
                                display: "inline",
                                fontWeight: "bold",
                              }}
                              variant="body2"
                            >
                              Price:{" "}
                            </Typography>
                          </Hidden>
                          ${door.price}
                        </Typography>
                        {door.itemizedAddons?.length ? (
                          <ItemizedCosts
                            quantity={door.quantity}
                            data={door.itemizedAddons}
                            unitPrice={door.price}
                          />
                        ) : null}
                      </StyledColDivEnd>

                      {door.quantity > 1 && (
                        <Typography variant="body2">
                          (${door.DUPforEach} ea.)
                        </Typography>
                      )}
                    </StyledColDiv2>
                  </>
                )}
              </StyledDivqp>
            </StyledCartLayoutDiv>
            <Divider />
          </div>
        ))}
      </StyledOrderUnits>
      <StyledColHidden>
        <Hidden mdDown>
          <Footer
            isSmallScreen={false}
            totalPanels={quote.totalPanels || 0}
            totalUnits={quote.totalUnits || 0}
          />
        </Hidden>

        <Hidden smUp>
          <Footer
            isSmallScreen={true}
            totalPanels={quote.totalPanels || 0}
            totalUnits={quote.totalUnits || 0}
          />
        </Hidden>
      </StyledColHidden>
    </StyledWrapDiv>
  );
};

export default DoorList;
